import React, { useEffect, useRef, useState } from 'react';
import CatalogImage from './CatalogImage';
import MatchBadge from './MatchBadge';
import { hasPrice } from './price';
import { lockBodyScroll } from './scrollLock';
import { ChevronLeftIcon, XIcon } from './icons';
import type { ProductLink, ShoppableProduct, StoreItem } from './types';

const ProductCard: React.FC<{
  product: StoreItem;
  selected?: boolean;
  onClick: () => void;
  onSimilar?: () => void;
  similar?: boolean;
}> = ({ product, selected, onClick, onSimilar, similar = false }) => (
  <article className={`overflow-hidden rounded-2xl border bg-white ${selected ? 'border-gray-900' : 'border-gray-200'}`}>
    <button type="button" className="block w-full text-left" onClick={onClick} aria-label={`View ${product.name}`}>
      <div className="relative aspect-[4/5] bg-gray-100 p-3">
        <CatalogImage src={product.image} alt={product.name} className="h-full w-full object-contain" />
      </div>
      <div className="px-3 pt-3">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
          <span className="truncate">{similar ? `Similar find · ${product.brand}` : product.brand}</span>
          {!similar && <MatchBadge matchType={product.matchType} />}
        </p>
        <p className="mt-1 line-clamp-2 min-h-10 text-sm font-medium leading-5">{product.name}</p>
        <p className="mt-1 text-sm">{hasPrice(product.price) ? product.price : 'Check retailer for price'}</p>
      </div>
    </button>
    <div className="flex items-center gap-2 px-3 pb-3 pt-3">
      <button type="button" onClick={onClick} className="min-h-10 flex-1 rounded-full bg-gray-950 px-3 text-xs font-medium text-white">
        View details
      </button>
      {onSimilar && (
        <button type="button" onClick={onSimilar} className="min-h-10 rounded-full border border-gray-200 px-3 text-xs font-medium">
          Similar
        </button>
      )}
    </div>
  </article>
);

interface ProductDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Everything shoppable in the current scene. */
  products: ShoppableProduct[];
  selectedProduct: ShoppableProduct | StoreItem | null;
  onSelectProduct: (product: ShoppableProduct | StoreItem | null) => void;
  similarTarget: ShoppableProduct | null;
  onShowSimilar: (product: ShoppableProduct) => void;
  onClearSimilar: () => void;
  cobranded?: boolean;
}

/** Right-hand drawer on desktop, bottom sheet on mobile: Shop the Look -> Item details -> Similar items.
 *  Laid out like the editorial demo (silvr-editor-purchase-experience-demo, Josh 2026-10-22): the
 *  purchase happens at the store, so the item view ends in one "Shop at <store>" button, no bag. */
const ProductDrawer: React.FC<ProductDrawerProps> = ({
  isOpen,
  onClose,
  products,
  selectedProduct,
  onSelectProduct,
  similarTarget,
  onShowSimilar,
  onClearSimilar,
  cobranded = true,
}) => {
  const dialogRef = useRef<HTMLElement>(null);
  // Which listing the shopper picked for the item on screen (size / price); the first by default.
  const [listingIndex, setListingIndex] = useState(0);
  useEffect(() => setListingIndex(0), [selectedProduct?.id]);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockBodyScroll();
    // Focus the sheet itself, not its close button: a focused X drew a ring around it on every open,
    // which read as a stray button shadow (Josh, SIL-1592). Tab still reaches the X first.
    dialogRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      unlock();
      if (restoreFocusRef.current?.isConnected) restoreFocusRef.current.focus();
    };
  }, [isOpen]);

  const relatedProduct = products.find((product) => product.id === selectedProduct?.id);
  const similarItems = similarTarget?.similar || relatedProduct?.similar || [];
  const showingSimilar = Boolean(similarTarget && selectedProduct?.id === similarTarget.id);
  const heading = showingSimilar ? 'Similar items' : selectedProduct ? 'Item details' : 'Shop the Look';

  return (
    <>
      <div
        className={`silvr-ui fixed inset-0 z-[100000] bg-black/30 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <section
        role="dialog"
        aria-modal={isOpen}
        aria-label={heading}
        aria-hidden={!isOpen}
        ref={dialogRef}
        tabIndex={-1}
        className={`silvr-ui fixed bottom-0 left-0 right-0 z-[100001] mx-auto flex outline-none max-h-[88dvh] w-full max-w-2xl flex-col rounded-t-3xl bg-white text-gray-900 shadow-2xl transition-[transform,opacity] duration-300 ease-out lg:inset-y-0 lg:left-auto lg:right-0 lg:mx-0 lg:max-h-none lg:w-[440px] lg:rounded-none ${isOpen ? 'translate-y-0 opacity-100 lg:translate-x-0' : 'translate-y-full opacity-0 lg:translate-x-full lg:translate-y-0'}`}
        style={{ pointerEvents: isOpen ? 'auto' : 'none', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4 sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Silvr</p>
            <h2 className="text-xl font-semibold">{heading}</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-gray-100" aria-label="Close product details">
            <XIcon />
          </button>
        </div>
        <div className="silvr-scroll min-h-0 flex-1 p-5 sm:p-6">
          {selectedProduct && !showingSimilar && (
            <button
              type="button"
              onClick={() => (similarTarget ? onSelectProduct(similarTarget) : onSelectProduct(null))}
              className="mb-4 flex min-h-10 items-center gap-1 text-sm font-medium"
            >
              <ChevronLeftIcon /> {similarTarget ? 'Back to similar items' : 'All items'}
            </button>
          )}
          {showingSimilar && (
            <button type="button" onClick={onClearSimilar} className="mb-4 flex min-h-10 items-center gap-1 text-sm font-medium">
              <ChevronLeftIcon /> Back to item
            </button>
          )}
          {showingSimilar ? (
            <>
              <p className="mb-4 text-sm text-gray-600">Based on {similarTarget?.name}</p>
              {similarItems.length ? (
                <div className="grid grid-cols-2 gap-3">
                  {similarItems.map((item) => (
                    <ProductCard key={item.id} product={item} similar onClick={() => onSelectProduct(item)} />
                  ))}
                </div>
              ) : (
                <p role="status" className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                  No similar options have been approved for this item yet.
                </p>
              )}
            </>
          ) : selectedProduct ? (
            <>
              <div className="relative flex min-h-[220px] items-center justify-center rounded-2xl bg-gray-100 p-5 sm:min-h-[230px]">
                <CatalogImage src={selectedProduct.image} alt={selectedProduct.name} className="max-h-[230px] w-full object-contain" />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
                    <span>{relatedProduct ? selectedProduct.brand : `Similar find · ${selectedProduct.brand}`}</span>
                    {relatedProduct && <MatchBadge matchType={selectedProduct.matchType} />}
                  </p>
                  <h3 className="mt-1 text-xl font-semibold">{selectedProduct.name}</h3>
                </div>
                {hasPrice(selectedProduct.price) && <p className="shrink-0 text-lg font-semibold">{selectedProduct.price}</p>}
              </div>
              {relatedProduct && similarItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => onShowSimilar(relatedProduct)}
                  className="mt-6 flex min-h-20 w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <strong className="block text-base">See similar items</strong>
                    <span className="text-sm text-gray-500">Based on this match</span>
                  </span>
                  <span className="flex shrink-0 -space-x-2">
                    {similarItems.slice(0, 3).map((item) => (
                      <CatalogImage key={item.id} src={item.image} alt={item.name} className="h-10 w-10 rounded-md border-2 border-white bg-gray-100 object-cover" />
                    ))}
                  </span>
                  <span aria-hidden="true" className="text-2xl text-gray-500">
                    ›
                  </span>
                </button>
              )}
              {!selectedProduct.links?.length && <p className="mt-4 text-xs leading-5 text-gray-500">Check current size, availability and final price at the retailer.</p>}
              {selectedProduct.priceNote && <p className="mt-2 text-xs leading-5 text-gray-500">{selectedProduct.priceNote}</p>}
              {(() => {
                const links = selectedProduct.links ?? [];
                const chosen: ProductLink | null = links[Math.min(listingIndex, Math.max(links.length - 1, 0))] ?? null;
                return (
                  <>
                    {links.length > 0 && (
                      <fieldset className="mt-4">
                        <legend className="text-xs font-semibold uppercase tracking-wider text-gray-500">{links.length === 1 ? 'Listing' : 'Choose a listing'}</legend>
                        <ul className="mt-2 flex flex-col gap-2">
                          {links.map((link, index) => {
                            const picked = index === listingIndex;
                            return (
                              <li key={link.url}>
                                <button
                                  type="button"
                                  onClick={() => setListingIndex(index)}
                                  aria-pressed={picked}
                                  className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border px-4 text-left text-sm ${picked ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'}`}
                                >
                                  <span className="min-w-0 truncate font-medium">
                                    {link.marketplace}
                                    {link.note ? <span className="ml-2 text-xs font-normal text-gray-500">{link.note}</span> : null}
                                  </span>
                                  <span className="shrink-0 text-gray-600">{[link.size ? `Size ${link.size}` : null, link.price].filter(Boolean).join(' · ')}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </fieldset>
                    )}
                    <a
                      href={chosen?.url ?? selectedProduct.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 flex min-h-14 w-full items-center justify-center rounded-xl bg-[#17171d] text-base font-medium text-white"
                      aria-label={`Shop ${selectedProduct.name} at ${chosen?.marketplace ?? selectedProduct.brand} in a new tab`}
                    >
                      {chosen ? `Shop at ${chosen.marketplace} ↗` : 'Shop at store ↗'}
                    </a>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-500">{products.length} items found</p>
              {products.length ? (
                <div className="grid grid-cols-2 gap-3">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => onSelectProduct(product)}
                      onSimilar={
                        product.similar.length
                          ? () => {
                              onSelectProduct(product);
                              onShowSimilar(product);
                            }
                          : undefined
                      }
                    />
                  ))}
                </div>
              ) : (
                <p role="status" className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                  No products are available in this scene right now.
                </p>
              )}
            </>
          )}
        </div>
        {cobranded && (
          <div className="shrink-0 border-t border-gray-100 bg-gray-50 px-6 py-3 text-center text-xs text-gray-500">
            Powered by <strong className="text-gray-900">Silvr</strong>
          </div>
        )}
      </section>
    </>
  );
};

export default ProductDrawer;
